import { isNil } from 'ramda'
import useSWR from 'swr'

import { useAppointments } from '@/modules/appointment/hooks/useAppointments'
import { useEncounters } from '@/modules/encounter/hooks/useEncounters'

import type { Appointment } from '@/modules/appointment/types/appointment.types'
import type { Encounter as HealthblocksEncounter } from '@/modules/encounter/types/encounter.types'

type Encounter = HealthblocksEncounter & { trigger: string }
type AppointmentOrEncounter = (Appointment | Encounter) & {
  type: 'encounter' | 'appointment'
}

export const useGetUpcomingAppointment = () => {
  const { upcomingAppointmentFetcher: appointmentFetcher } = useAppointments()
  const { upcomingEncounterFetcher: encountersFetcher } = useEncounters()

  const upcomingAppointmentFetcher = async () => {
    return appointmentFetcher().then((value) => value.data)
  }

  const upcomingEncounterFetcher = async () => {
    return encountersFetcher().then((value) => value.data)
  }

  const parallelFetcher = async (): Promise<AppointmentOrEncounter | null> => {
    return await Promise.all([upcomingAppointmentFetcher(), upcomingEncounterFetcher()]).then(
      ([appointment, encounter]) => {
        if (appointment && isNil(encounter)) {
          return { ...appointment, type: 'appointment' } as AppointmentOrEncounter
        }

        if (encounter && isNil(appointment)) {
          return { ...encounter, type: 'encounter' } as AppointmentOrEncounter
        }

        if (appointment && encounter) {
          return new Date(appointment.start) < new Date(encounter.start)
            ? { ...appointment, type: 'appointment' }
            : { ...encounter, type: 'encounter' }
        }

        // Return null (not undefined) so SWR treats this as a valid cached result
        // and does not show a loading state on remount.
        return null
      },
    )
  }

  return useSWR('upcoming-appointment', parallelFetcher)
}
